import { keccak256, toBytes } from 'viem';

export const READY_FILE = {
  spec: 'agent-ready',
  version: '0.1-concept',
  vendor: 'api.example',
  updatedAt: '2026-10-27',
  docsFormat: 'text/markdown',
  tasks: [
    { task: 'Take a test payment', quickstart: 'https://api.example/docs/quickstart-payments.md' },
    { task: 'Create a customer', quickstart: 'https://api.example/docs/quickstart-customers.md' },
  ],
  promises: {
    noHumanSignIn: { endpoint: '/v1/agent-signup', accepts: 'signed-setup-approval' },
    safeRetries: { header: 'Idempotency-Key', windowHours: 24 },
    honestFailures: { statusCodes: true, verifyEndpoint: '/v1/setup/verify' },
    errorFormat: { contentType: 'application/problem+json', fields: ['type', 'title', 'detail', 'retryable'] },
    retryAfter: { header: 'Retry-After', onStatus: [429, 503] },
  },
  claims: { endpoint: 'https://api.example/.well-known/ax-claims', receipts: 'signed' },
};

export const PROMISES = [
  ['noHumanSignIn', 'Sign-in with no human step'],
  ['safeRetries', 'Safe retries'],
  ['honestFailures', 'Honest failure codes'],
  ['errorFormat', 'Standard error format'],
  ['retryAfter', 'Retry-after signal'],
];

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

function vendor(broken) {
  let n = 0;
  const seen = {};
  return {
    signup: () => (broken ? { status: 302, location: '/login?verify-email=1' } : { status: 201, body: { testKey: 'sk_test_demo' } }),
    charge: (key) => (broken ? { status: 200, id: `ch_${++n}` } : { status: 200, id: (seen[key] ||= `ch_${++n}`) }),
    setupStep: () => (broken ? { status: 200, body: { ok: true } } : { status: 422, body: { ok: false } }),
    verify: () => ({ status: 200, body: { webhookConfigured: false } }),
    badRequest: () => (broken ? { status: 400, text: 'request failed' } : { status: 400, json: { type: 'invalid_amount', title: 'Amount must be positive', detail: 'amount=-5', retryable: false } }),
    burst: () => (broken ? { status: 429, headers: {} } : { status: 429, headers: { 'Retry-After': '2' } }),
  };
}

export async function runChecks(broken, onStep) {
  const v = vendor(broken);
  const out = [];
  const push = async (id, ok, evidence) => { await wait(350); out.push({ id, ok, evidence }); onStep([...out]); };
  const s = v.signup();
  await push('noHumanSignIn', s.status === 201, s.status === 201 ? 'POST /v1/agent-signup with a signed setup approval returned 201 and a test key.' : `POST /v1/agent-signup returned ${s.status}, redirecting to ${s.location}. An agent cannot finish this alone.`);
  const a = v.charge('idem-1');
  const b = v.charge('idem-1');
  await push('safeRetries', a.id === b.id, a.id === b.id ? `Sent the same request twice with one Idempotency-Key. Both returned ${a.id}. No duplicate.` : `Sent the same request twice with one Idempotency-Key. Got ${a.id} and ${b.id}. The retry created a duplicate charge.`);
  const st = v.setupStep();
  const vf = v.verify();
  const honest = st.status >= 400 || vf.body.webhookConfigured;
  await push('honestFailures', honest, honest ? `Webhook setup returned ${st.status} and the verify call agrees it is not configured.` : `Webhook setup returned ${st.status} {"ok":true}, but /v1/setup/verify says it is not configured. The step reported success and failed.`);
  const e = v.badRequest();
  await push('errorFormat', !!e.json, e.json ? `Bad request returned ${e.status} with type, title, detail, and retryable=false.` : `Bad request returned ${e.status} with the text "${e.text}". Nothing an agent can act on.`);
  const r = v.burst();
  await push('retryAfter', !!r.headers['Retry-After'], r.headers['Retry-After'] ? `A burst of requests returned 429 with Retry-After: ${r.headers['Retry-After']}.` : 'A burst of requests returned 429 with no Retry-After. The agent has to guess when to try again.');
  return out;
}

export const claimIdFor = (promiseId, evidence) => keccak256(toBytes(`api.example|${promiseId}|${evidence}`));

export async function recheckPromise(promiseId) {
  const out = await runChecks(false, () => {});
  return out.find((r) => r.id === promiseId);
}
